// src/components/WhoIsThisFor/WhoIsThisFor.jsx

import React, { useMemo } from "react";
import { deepMerge, useSiteContent } from "../../content/SiteContentContext";
import {
  FaBuilding,
  FaMicrophone,
  FaGuitar,
  FaCamera,
  FaEuroSign,
  FaTools,
} from "react-icons/fa";

const translations = {
  fi: {
    eyebrow: "Palvelut",
    title: "Mihin tarvitset verkkosivut?",
    lead:
      "Valitse sinulle sopivin vaihtoehto. Jokainen sivusto suunnitellaan täysin räätälöitynä tavoitteiden, sisällön ja tarvittavien toimintojen mukaan.",
    labels: {
      need: "Tarve:",
      solution: "Mitä teen:",
    },
    segments: [
      {
        key: "business",
        title: "Yritykselle tai palveluntarjoajalle",
        need:
          "Tarvitset uskottavan ja selkeän sivuston, joka esittelee palvelusi ja ohjaa kävijää ottamaan yhteyttä.",
        solution:
          "Suunnittelen yrityksellesi räätälöidyn sivuston, jossa rakenne, sisältö, SEO ja yhteydenottopolku tukevat liiketoimintaasi.",
        cta: "Verkkosivut yrityksille →",
        path: "/verkkosivut-yrityksille/",
      },
      {
        key: "musician",
        title: "Muusikolle tai artistille",
        need:
          "Tarvitset oman sivuston, jossa musiikki, videot, kuvat, keikat ja yhteydenotto löytyvät helposti.",
        solution:
          "Rakennan artistille räätälöidyn sivuston, joka tukee keikkamyyntiä, Google-näkyvyyttä ja somesta tulevaa liikennettä.",
        cta: "Verkkosivut muusikoille →",
        path: "/verkkosivut-muusikoille/",
      },
      {
        key: "band",
        title: "Bändille",
        need:
          "Tarvitsette sivuston, joka näyttää ja kuulostaa omalta bändiltänne ja tekee keikan ostamisesta helppoa.",
        solution:
          "Rakennan bändille oman kokonaisuuden, jossa kokoonpanot, musiikki, videot, referenssit ja yhteydenotto tukevat keikkamyyntiä.",
        cta: "Verkkosivut bändeille →",
        path: "/verkkosivut-bandeille/",
      },
      {
        key: "photographer",
        title: "Valokuvaajalle",
        need:
          "Tarvitset visuaalisen sivuston, jossa kuvasi, palvelusi ja oma tyylisi pääsevät kunnolla esiin.",
        solution:
          "Suunnittelen valokuvaajalle sivuston, jossa portfolio, palvelut, paikallinen löydettävyys ja yhteydenotto muodostavat yhtenäisen kokonaisuuden.",
        cta: "Verkkosivut valokuvaajille →",
        path: "/valokuvaajan-verkkosivut/",
      },
      {
        key: "pricing",
        title: "Haluatko tietää hinnan?",
        need:
          "Haluat hahmottaa, mitä verkkosivuprojekti voisi maksaa ja mistä hinta muodostuu.",
        solution:
          "Näet suuntaa-antavat hintatasot, mitä toteutukseen yleensä kuuluu ja voit arvioida projektisi laajuutta ennen yhteydenottoa.",
        cta: "Katso verkkosivujen hinnat →",
        path: "/verkkosivujen-hinta/",
      },
    ],
    existing: {
      title: "Sivut on jo, mutta ne eivät tuo yhteydenottoja?",
      text:
        "Voin käydä läpi rakenteen, viestit, toimintakehotukset, SEO:n ja mittauksen ja ehdottaa, mitä kannattaa korjata ensin.",
      cta: "Kerro nykyisestä sivustostasi →",
    },
  },

  en: {
    eyebrow: "Services",
    title: "What do you need a website for?",
    lead:
      "Choose the option that fits you best. Every website is designed and built around your goals, content and required functionality.",
    labels: {
      need: "Need:",
      solution: "What I do:",
    },
    segments: [
      {
        key: "business",
        title: "For a business or service provider",
        need:
          "You need a credible and clear website that presents your services and guides visitors toward getting in touch.",
        solution:
          "I design a custom website where structure, content, SEO and the contact path support your business goals.",
        cta: "Websites for businesses →",
        path: "/verkkosivut-yrityksille/",
      },
      {
        key: "musician",
        title: "For a musician or artist",
        need:
          "You need your own website where music, videos, photos, gigs and contact information are easy to find.",
        solution:
          "I build a custom artist website that supports bookings, Google visibility and traffic from social media.",
        cta: "Websites for musicians →",
        path: "/verkkosivut-muusikoille/",
      },
      {
        key: "band",
        title: "For a band",
        need:
          "You need a website that looks and feels like your band and makes booking you easy.",
        solution:
          "I build a custom band website where line-ups, music, videos, references and contact information support bookings.",
        cta: "Websites for bands →",
        path: "/verkkosivut-bandeille/",
      },
      {
        key: "photographer",
        title: "For a photographer",
        need:
          "You need a visual website where your photography, services and personal style have room to stand out.",
        solution:
          "I design a photographer website where the portfolio, services, local search visibility and contact path work together.",
        cta: "Websites for photographers →",
        path: "/valokuvaajan-verkkosivut/",
      },
      {
        key: "pricing",
        title: "Want to know the price?",
        need:
          "You want to understand what a website project may cost and what affects the final price.",
        solution:
          "See indicative price levels, what is usually included and estimate the scope of your project before getting in touch.",
        cta: "See website pricing →",
        path: "/verkkosivujen-hinta/",
      },
    ],
    existing: {
      title: "Already have a website, but it does not generate inquiries?",
      text:
        "I can review the structure, messaging, calls to action, SEO and tracking and suggest what should be improved first.",
      cta: "Tell me about your current website →",
    },
  },

  es: {
    eyebrow: "Servicios",
    title: "¿Para qué necesitas una web?",
    lead:
      "Elige la opción que mejor encaje contigo. Cada web se diseña y desarrolla a medida según tus objetivos, contenidos y funcionalidades necesarias.",
    labels: {
      need: "Necesidad:",
      solution: "Qué hago:",
    },
    segments: [
      {
        key: "business",
        title: "Para una empresa o proveedor de servicios",
        need:
          "Necesitas una web clara y creíble que presente tus servicios y guíe al visitante hacia el contacto.",
        solution:
          "Diseño una web a medida donde la estructura, el contenido, el SEO y la ruta de contacto apoyan los objetivos del negocio.",
        cta: "Webs para empresas →",
        path: "/verkkosivut-yrityksille/",
      },
      {
        key: "musician",
        title: "Para un músico o artista",
        need:
          "Necesitas una web propia donde música, vídeos, fotos, conciertos y contacto sean fáciles de encontrar.",
        solution:
          "Creo una web de artista a medida que apoya las reservas, la visibilidad en Google y el tráfico desde redes sociales.",
        cta: "Webs para músicos →",
        path: "/verkkosivut-muusikoille/",
      },
      {
        key: "band",
        title: "Para una banda",
        need:
          "Necesitáis una web que tenga la identidad de vuestra banda y facilite la contratación.",
        solution:
          "Creo una web a medida donde formaciones, música, vídeos, referencias y contacto apoyan la venta de actuaciones.",
        cta: "Webs para bandas →",
        path: "/verkkosivut-bandeille/",
      },
      {
        key: "photographer",
        title: "Para un fotógrafo",
        need:
          "Necesitas una web visual donde tus fotografías, servicios y estilo propio tengan protagonismo.",
        solution:
          "Diseño una web donde portfolio, servicios, visibilidad local y contacto formen un conjunto coherente.",
        cta: "Webs para fotógrafos →",
        path: "/valokuvaajan-verkkosivut/",
      },
      {
        key: "pricing",
        title: "¿Quieres saber el precio?",
        need:
          "Quieres saber cuánto podría costar un proyecto web y qué factores influyen en el precio.",
        solution:
          "Consulta precios orientativos, qué suele incluir el proyecto y estima su alcance antes de contactar.",
        cta: "Ver precios de páginas web →",
        path: "/verkkosivujen-hinta/",
      },
    ],
    existing: {
      title: "¿Ya tienes web, pero no genera contactos?",
      text:
        "Puedo revisar la estructura, los mensajes, las llamadas a la acción, el SEO y la medición y decirte qué conviene mejorar primero.",
      cta: "Cuéntame sobre tu web actual →",
    },
  },
};

const icons = {
  business: FaBuilding,
  musician: FaMicrophone,
  band: FaGuitar,
  photographer: FaCamera,
  pricing: FaEuroSign,
};

const WhoIsThisFor = ({ lang = "fi", prefix = "" }) => {
  const siteContent = useSiteContent();
  const copy = useMemo(() => {
    return deepMerge(
      translations[lang] || translations.fi,
      siteContent?.pages?.whoIsThisFor?.[lang] || {}
    );
  }, [lang, siteContent]);

  // prefix on esimerkiksi "", "/en" tai "/es".
  const buildPath = (path) => `${prefix || ""}${path}`;

  const scrollToContact = (event) => {
    event.preventDefault();

    const target = document.getElementById("contact");

    if (!target) return;

    const headerOffset = 110;
    const targetPosition =
      target.getBoundingClientRect().top + window.scrollY - headerOffset;

    window.scrollTo({
      top: targetPosition,
      behavior: "smooth",
    });
  };

  const fireEvent = (name, params = {}) => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", name, params);
    }
  };

  return (
    <section
      className="mx-auto w-full scroll-mt-24 px-2 pb-24 sm:px-4 md:px-6 lg:px-12 xl:px-16"
      aria-labelledby="who-is-this-for-title"
    >
      <div className="mx-auto w-full max-w-6xl">
        <div className="rounded-[2rem] border border-black/10 bg-white/70 px-6 py-8 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] md:px-10 md:py-10">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-black/50 dark:text-white/45">
              {copy.eyebrow}
            </p>

            <h2
              id="who-is-this-for-title"
              className="mt-3 text-3xl font-bold text-black dark:text-white md:text-4xl"
            >
              {copy.title}
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-base font-light leading-relaxed text-black/75 dark:text-white/75 md:text-lg">
              {copy.lead}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {copy.segments.map((segment) => {
              const Icon = icons[segment.key] || FaBuilding;

              return (
                <article
                  key={segment.key}
                  className="group flex h-full flex-col rounded-2xl border border-black/10 bg-white/80 px-5 py-5 transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-white/5"
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-black/[0.04] text-lg text-lime-600 dark:border-white/10 dark:bg-white/5 dark:text-lime-400">
                    <Icon aria-hidden="true" />
                  </div>

                  <h3 className="mt-5 text-lg font-bold leading-snug text-black dark:text-white">
                    {segment.title}
                  </h3>

                  <p className="mt-4 text-sm leading-relaxed text-black/65 dark:text-white/65">
                    <span className="font-bold text-black/80 dark:text-white/80">
                      {copy.labels.need}
                    </span>{" "}
                    {segment.need}
                  </p>

                  <p className="mt-3 text-sm leading-relaxed text-black/70 dark:text-white/70">
                    <span className="font-bold text-black/90 dark:text-white/90">
                      {copy.labels.solution}
                    </span>{" "}
                    {segment.solution}
                  </p>

                  <div className="mt-auto pt-6">
                    <a
                      href={buildPath(segment.path)}
                      onClick={() =>
                        fireEvent("palvelulinkki_klikattu", {
                          kohde: segment.key,
                          kieli: lang,
                        })
                      }
                      className="inline-flex items-center font-bold text-black transition hover:translate-x-1 dark:text-white"
                    >
                      {segment.cta}
                    </a>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-6 rounded-2xl border border-black/10 bg-white/80 px-6 py-6 dark:border-white/10 dark:bg-white/5 md:px-8">
            <div className="grid gap-5 md:grid-cols-[auto_1fr_auto] md:items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 bg-black/[0.04] text-lg text-lime-600 dark:border-white/10 dark:bg-white/5 dark:text-lime-400">
                <FaTools aria-hidden="true" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-black dark:text-white">
                  {copy.existing.title}
                </h3>

                <p className="mt-2 max-w-3xl text-sm leading-relaxed text-black/65 dark:text-white/65 md:text-base">
                  {copy.existing.text}
                </p>
              </div>

              <a
                href="#contact"
                onClick={(event) => {
                  fireEvent("nykyinen_sivusto_yhteydenotto_klikattu", {
                    kieli: lang,
                  });
                  scrollToContact(event);
                }}
                className="inline-flex items-center justify-center rounded-full bg-black px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:shadow-lg dark:bg-white dark:text-black"
              >
                {copy.existing.cta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoIsThisFor;
